/*
 * Slideshow Section — Swiper-based slideshow for sections/slideshow.liquid.
 *
 * Separate from assets/slideshow.js, which defines the
 * <slideshow-component> custom element used by product galleries,
 * collection links, and other places that need a scroll-snap
 * carousel. This module is dedicated to the standalone Slideshow
 * section and uses the same Swiper bundle the other section sliders
 * (Multicolumn, Testimonials, Product Row) load.
 *
 * Per-section pattern: each `[data-slideshow-section]` gets its own
 * Swiper. Re-runs on shopify:section:load so customizer edits
 * don't leave a zombie instance attached to recycled DOM.
 *
 * DOM contract:
 *   [data-slideshow-section]
 *     .slideshow-section__arrows
 *       button.slideshow-control--previous
 *       button.slideshow-control--next
 *     .swiper.slideshow-section__swiper
 *       .swiper-wrapper
 *         .swiper-slide.slideshow-section__slide  (one per _slide block)
 *       .slideshow-section__pagination
 *
 * Data attributes the JS reads from the wrap element:
 *   data-effect              "slide" | "fade"
 *   data-speed               transition speed in ms
 *   data-loop                "true" | "false"
 *   data-autoplay            "true" | "false"
 *   data-autoplay-delay      autoplay delay in seconds
 *   data-show-navigation     "true" | "false"
 *   data-show-pagination     "true" | "false"
 */

const Swiper = window.Swiper;

const SELECTOR = '[data-slideshow-section]';
const INSTANCE_KEY = '_slideshowSectionInstance';

class SlideshowSection {
  constructor(root) {
    this.root = root;
    this.swiper = null;
    this.init();
  }

  init() {
    const swiperEl = this.root.querySelector('.slideshow-section__swiper');
    if (!swiperEl) return;

    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper || wrapper.children.length === 0) return;

    const ds = this.root.dataset;
    const effect = ds.effect === 'fade' ? 'fade' : 'slide';
    const speed = parseFloat(ds.speed) || 600;
    // Loop mode duplicates slides; with a single slide the clones
    // overlap the original and arrow / pagination behaviour breaks.
    // Force-disable when there isn't more than one slide regardless
    // of the merchant's toggle.
    const slideCount = wrapper.children.length;
    const loop = ds.loop === 'true' && slideCount > 1;
    const autoplay = ds.autoplay === 'true';
    const autoplayDelay = (parseFloat(ds.autoplayDelay) || 5) * 1000;
    const showNavigation = ds.showNavigation === 'true';
    const showPagination = ds.showPagination === 'true';

    /** @type {any} */
    const options = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed,
      effect,
      // Fade needs crossFade for the outgoing slide not to clip
      // the incoming one mid-transition.
      fadeEffect: effect === 'fade' ? { crossFade: true } : undefined,
      loop,
      autoplay: autoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      // watchOverflow drops the slider into "locked" mode when
      // there's only one slide. We track lock via events and toggle
      // a data attribute that CSS uses to hide arrows + pagination.
      watchOverflow: true,
      observer: true,
      observeParents: true,
    };

    if (showNavigation) {
      const prevEl = this.root.querySelector('.slideshow-control--previous');
      const nextEl = this.root.querySelector('.slideshow-control--next');
      if (prevEl && nextEl) {
        options.navigation = { prevEl, nextEl };
      }
    }

    if (showPagination) {
      const paginationEl = this.root.querySelector('.slideshow-section__pagination');
      if (paginationEl) {
        options.pagination = { el: paginationEl, clickable: true };
      }
    }

    this.swiper = new Swiper(swiperEl, options);

    const setLocked = (locked) => {
      this.root.dataset.locked = locked ? 'true' : 'false';
    };
    setLocked(this.swiper.isLocked);
    this.swiper.on('lock', () => setLocked(true));
    this.swiper.on('unlock', () => setLocked(false));

    this.root[INSTANCE_KEY] = this;
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new SlideshowSection(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
